import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Avatar } from '@teetsh/app/src/components/AvatarCard/Avatar';
import ReactTable from '@teetsh/app/src/components/Table/ReactTable';
import { LivretEval } from '@teetsh/app/src/containers/Livrets/types/livret';
import livretsUrls from '@teetsh/app/src/containers/Livrets/urls';
import sortFctReactTable from '@teetsh/app/src/utils/sortFctReactTable';
import SchoolyearLink from 'components/Link/SchoolyearLink';
import { useGetQuerySort } from 'containers/Livrets/LivretEval/useGetQuerySort';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import 'twin.macro';
import { EleveLivretActions } from './EleveLivretActions';
import { LivretStatus } from './LivretStatus';
import { LSUStatusBadge } from './LSUStatusBadge';
import { EleveBasicInfo } from './types';

interface Props {
  livretEval: LivretEval;
  eleves: EleveBasicInfo[];
  getEleveLink?: (eleveId: string) => string;
  readonly?: boolean;
}

export function LivretEvalTable(props: Props) {
  const { livretEval, eleves, getEleveLink, readonly } = props;
  const { sortField, desc, onChangeSort } = useGetQuerySort();
  const { t } = useTranslation(['carnet']);

  const columns = useMemo(
    () => [
      {
        id: 'picture',
        header: () => '',
        size: 64,
        cell: p => {
          const eleve = p.row.original as EleveBasicInfo;
          return (
            <Avatar
              picture={eleve?.picture}
              fill
              tw="rounded-md"
              dimensions={{ width: 40, height: 40 }}
            />
          );
        },
      },
      {
        accessorKey: 'familyName',
        header: () => t('carnet:lastName'),
        sortingFn: sortFctReactTable,
        cell: p => {
          const {
            row: { original },
          } = p;
          return (
            <SchoolyearLink
              withSchoolContext={!getEleveLink}
              preserveQueryParams
              to={
                getEleveLink
                  ? getEleveLink(original?.id)
                  : `${livretsUrls.livretEleveDetails(
                      livretEval.id as string,
                      original?.id as string,
                    )}`
              }
              tw="text-neutral-intense truncate hover:font-semibold cursor-pointer"
            >
              {p.getValue()}
            </SchoolyearLink>
          );
        },
      },
      {
        accessorKey: 'givenName',
        header: () => t('carnet:firstName'),
        sortingFn: sortFctReactTable,
        cell: p => {
          const {
            row: { original },
          } = p;
          return (
            <SchoolyearLink
              withSchoolContext={!getEleveLink}
              preserveQueryParams
              to={
                getEleveLink
                  ? getEleveLink(original?.id)
                  : `${livretsUrls.livretEleveDetails(
                      livretEval.id as string,
                      original?.id as string,
                    )}`
              }
              tw="text-neutral-intense truncate hover:font-semibold cursor-pointer"
            >
              {p.getValue()}
            </SchoolyearLink>
          );
        },
      },
      {
        accessorKey: 'status',
        header: () => t('carnet:status'),
        sortingFn: sortFctReactTable,
        cell: ({ row }) => <LivretStatus status={row.getValue('status')} />,
      },
      {
        accessorKey: 'statusLsu',
        header: () => t('carnet:livret.lsuCompatibility'),
        sortingFn: sortFctReactTable,
        cell: ({ row }) => (
          <LSUStatusBadge status={row.getValue('statusLsu')} />
        ),
      },
      {
        id: 'actions',
        header: () => '',
        cell: p => {
          const eleve = p.row.original as EleveBasicInfo;
          const matchingLivretEleve = livretEval.livretsEleves.find(
            livretEleve => livretEleve.eleveId === eleve.id,
          );
          return (
            <div tw="text-right">
              <EleveLivretActions
                readonly={readonly}
                getEleveLink={getEleveLink}
                eleveId={eleve.id}
                livretEvalId={livretEval.id}
                livretEleve={matchingLivretEleve}
                status={matchingLivretEleve?.status}
              />
            </div>
          );
        },
      },
    ],
    [JSON.stringify(livretEval)],
  );

  const sorting = useMemo(
    () => [
      {
        id: sortField || 'familyName',
        desc: desc || false,
      },
    ],
    [sortField, desc],
  );

  const tableInstance = useReactTable({
    data: eleves,
    columns,
    state: {
      sorting,
    },
    onSortingChange: (v: any) => {
      const order = v()[0];
      onChangeSort(order.id, order.desc ? 'desc' : 'asc');
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return <ReactTable tableInstance={tableInstance} />;
}
